import { Autocomplete, Chip, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { Image } from '../../../../../graphql/generated'
import useAddUserTags from '../hooks/useAddUserTags'
import useGetUserTags from '../hooks/useGetUserTags'
import useRemoveUserTags from '../hooks/useRemoveUserTags'
import useUserSettings from '../../../hooks/useUserSettings'

interface Props {
  currentImage?: Image | null
  handleSaveTags: (tags: string[]) => void
}

export default function TagManagement({ handleSaveTags, currentImage = undefined }: Readonly<Props>) {
  const { mutation: addUserTagsMutation } = useAddUserTags()
  const { mutation: removeUserTagsMutation } = useRemoveUserTags()
  const { isMediaTagsDisabled } = useUserSettings()
  const [open, setOpen] = useState<boolean>(false)
  const [userTags, setUserTags] = useState<string[]>([])
  const [tagPattern, setTagPattern] = useState('')
  const [autocompleteValue, setAutocompleteValue] = useState('')
  const { tags: availableTagsList } = useGetUserTags(tagPattern)

  useEffect(() => {
    if (currentImage && currentImage.userTags) {
      setUserTags(currentImage.userTags as string[])
    }
  }, [currentImage])

  const handleTagDelete = (tag: string) => {
    if (currentImage?.userTags && currentImage.userTags.indexOf(tag) > -1) {
      const variables = {
        imageIds: [currentImage.id],
        tags: [tag]
      }
      removeUserTagsMutation({ variables })
    }

    const newUserTags = userTags.filter((userTag) => userTag !== tag)
    setUserTags(newUserTags)
    handleSaveTags(newUserTags)
  }

  const setOpenHandler = () => setOpen(true)
  const setCloseHandler = () => setOpen(false)
  return (
    <div style={{ padding: 16 }}>
      <Autocomplete
        open={open}
        disabled={isMediaTagsDisabled}
        onOpen={setOpenHandler}
        onClose={setCloseHandler}
        isOptionEqualToValue={(option: string, value: string) => option === value}
        options={availableTagsList as string[]}
        value={autocompleteValue}
        inputValue={tagPattern}
        renderInput={(params) => (
          <TextField
            label="Add tag"
            placeholder="Add tag..."
            disabled={isMediaTagsDisabled}
            fullWidth
            id={params.id}
            InputLabelProps={params.InputLabelProps}
            InputProps={{
              ...params.InputProps,
              inputProps: params.inputProps
            }}
          />
        )}
        onInputChange={(_e, str) => {
          setTagPattern(str)
        }}
        onChange={(_e, newValue: string | null) => {
          if (newValue && newValue.trim() && currentImage?.id) {
            const newTagValue = newValue.trim()
            const newUserTags = [...userTags, newTagValue]
            setUserTags(newUserTags)
            if (availableTagsList.indexOf(newTagValue) === -1) {
              const variables = {
                imageIds: [currentImage.id],
                tags: [newTagValue]
              }
              addUserTagsMutation({ variables })
              handleSaveTags(newUserTags)
            } else {
              handleSaveTags(newUserTags)
            }
            setAutocompleteValue('')
            setTagPattern('')
          }
        }}
        filterOptions={(options, params) => {
          const newOptions = options.filter((option) => userTags.indexOf(option) === -1)
          if (params.inputValue !== '') {
            newOptions.push(params.inputValue)
          }
          return newOptions
        }}
        selectOnFocus
        clearOnBlur
        freeSolo
      />
      <div style={{ marginTop: '16px' }}>
        {userTags.map((tag) => (
          <Chip key={tag} color="primary" sx={{ marginLeft: '4px', marginBottom: '4px' }} label={tag} onDelete={() => handleTagDelete(tag)} />
        ))}
      </div>
    </div>
  )
}
